import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { decodeData, encodeData } from '../../../utils/encodeAndEncode';
import { toast } from 'react-toastify';
import { getAllQuestionsForClassroomAssessmentForUser, submitClassroomAssessment } from '../../../services/userService';
import NoData from '../../../components/common/NoData';

function AssessmentQuiz() {
    const navigate = useNavigate();
    const { encodedAssessmentId } = useParams();
    const [searchParams] = useSearchParams();
    const encodedClassroomId = searchParams.get('classroomid');
    const sessionNameFromQuery = searchParams.get('session') || searchParams.get('sessionname') || searchParams.get('classroomname');

    const assessment_id = decodeData(decodeURIComponent(encodedAssessmentId));
    const classroom_id = encodedClassroomId ? decodeData(decodeURIComponent(encodedClassroomId)) : null;

    const [assessmentData, setAssessmentData] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [progress, setProgress] = useState(0);
    const [answers, setAnswers] = useState({});
    const [submissionResult, setSubmissionResult] = useState(null);
    const [timeLeft, setTimeLeft] = useState(null);
    const [quizStarted, setQuizStarted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isSubmittingRef = useRef(false);

    const userId = JSON.parse(localStorage.getItem('user'))?.id;

    const getTimerStorageKey = () =>
        `assessment_timer_end_v1:${userId || 'anon'}:${classroom_id || 'na'}:${assessment_id || 'na'}`;

    const clearStoredTimer = () => {
        try {
            localStorage.removeItem(getTimerStorageKey());
        } catch (_) {}
    };

    const getStoredEndTimeMs = () => {
        try {
            const stored = localStorage.getItem(getTimerStorageKey());
            const parsed = stored ? Number(stored) : NaN;
            return Number.isFinite(parsed) ? parsed : null;
        } catch (_) {
            return null;
        }
    };

    const setStoredEndTimeMs = (endTimeMs) => {
        try {
            localStorage.setItem(getTimerStorageKey(), String(endTimeMs));
        } catch (_) {}
    };

    // Ref to store current answers for timer callback
    const answersRef = useRef({});

    // Keep ref in sync with answers state
    useEffect(() => {
        answersRef.current = answers;
    }, [answers]);

    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                const payload = {
                    assessment_id: assessment_id,
                    class_id: classroom_id
                };

                const response = await getAllQuestionsForClassroomAssessmentForUser(payload);
                // console.log("Assessment Questions Response:-------------------", response);
                if (response.success) {
                    setAssessmentData(response.data.assessment);

                    // Persist timer across refresh by storing an absolute endTime in localStorage
                    // (client-side persistence only; for strict anti-cheat, enforce attempt timing server-side)
                    if (response.data.assessment.duration > 0) {
                        const durationSeconds = response.data.assessment.duration * 60;
                        const now = Date.now();
                        let endTimeMs = getStoredEndTimeMs();

                        if (!endTimeMs) {
                            endTimeMs = now + (durationSeconds * 1000);
                            setStoredEndTimeMs(endTimeMs);
                        }

                        const remaining = Math.max(0, Math.ceil((endTimeMs - now) / 1000));
                        setTimeLeft(remaining);

                        // If timer already expired (e.g. refresh after time is up), do not reset.
                        // Let the timer effect auto-submit immediately.
                    } else {
                        setTimeLeft(null);
                        clearStoredTimer();
                    }

                    setQuizStarted(true);
                }
            } catch (error) {
                console.error("Error fetching assessment questions:", error);
            }
        };

        if (assessment_id && classroom_id) {
            fetchQuestions();
        }
    }, [assessment_id, classroom_id]);

    // Timer effect
    useEffect(() => {
        let timer;
        if (quizStarted && timeLeft !== null && !submissionResult) {
            timer = setInterval(() => {
                const endTimeMs = getStoredEndTimeMs();
                const now = Date.now();
                const remaining = endTimeMs ? Math.max(0, Math.ceil((endTimeMs - now) / 1000)) : 0;

                setTimeLeft(remaining);

                if (remaining === 25) {
                    toast.warning(' Only 25 seconds remaining!', {
                        toastId: 'timer-25',
                        position: "top-center",
                        autoClose: 3000,
                    });
                }

                if (remaining <= 0) {
                    clearInterval(timer);
                    clearStoredTimer();
                    handleSubmit(true);
                }
            }, 1000);
        }

        return () => {
            if (timer) clearInterval(timer);
        };
    }, [quizStarted, submissionResult]); // Add submissionResult to dependencies

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleOptionSelect = (optionId) => {
        const currentQuestion = assessmentData.questions[currentQuestionIndex];

        setSelectedAnswer(optionId);
        setAnswers(prev => {
            const newAnswers = {
                ...prev,
                [currentQuestionIndex]: optionId
            };
            // Update ref with latest answers
            answersRef.current = newAnswers;
            // console.log("Updated Answers:", newAnswers);
            return newAnswers;
        });

        // Calculate progress based on unique answered questions
        const answeredQuestionsCount = Object.keys(answers).length + 1; // +1 for current selection
        const progressPercentage = (answeredQuestionsCount / assessmentData.total_questions) * 100;
        setProgress(progressPercentage);

        // Commented multi-line console.log);
    };

    const handleNext = () => {
        if (currentQuestionIndex < (assessmentData?.questions?.length - 1)) {
            setCurrentQuestionIndex(prev => prev + 1);
            // Show previously selected answer if it exists
            setSelectedAnswer(answers[currentQuestionIndex + 1] || null);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
            // Show previously selected answer if it exists
            setSelectedAnswer(answers[currentQuestionIndex - 1] || null);
        }
    };

    const handleSubmit = async (isAutoSubmit = false) => {
        if (isSubmittingRef.current || submissionResult) {
            return;
        }

        isSubmittingRef.current = true;
        setIsSubmitting(true);

        const currentAnswers = isAutoSubmit ? answersRef.current : answers;

        const answersArray = Object.entries(currentAnswers).map(([questionIndex, optionId]) => ({
            question_id: assessmentData.questions[parseInt(questionIndex)].id,
            option: optionId.toString()
        }));

        const submissionPayload = {
            class_id: classroom_id,
            assessment_id: assessment_id,
            user_id: userId,
            quiz_answers: answersArray
        };

        try {
            const response = await submitClassroomAssessment(submissionPayload);
            if (response?.success) {
                setSubmissionResult(response.data);
                clearStoredTimer();
                toast.success('Assessment submitted successfully!', {
                    toastId: 'assessment-submitted',
                });
            } else {
                toast.error('Failed to submit assessment. Please try again.');
            }
        } catch (error) {
            console.error("Error submitting assessment:", error);
            toast.error('Error submitting assessment. Please try again.');
        } finally {
            setIsSubmitting(false);
            isSubmittingRef.current = false; // optional if retry is ever allowed
        }
    };

    if (submissionResult) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-4xl mx-auto py-8 px-4">
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-6 sm:p-8 border-b border-gray-100 bg-gray-50/40">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Assessment Result</h1>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {assessmentData?.assessment_name}
                                        {(sessionNameFromQuery || assessmentData?.classroom_name || assessmentData?.session_name) ? (
                                            <>
                                                {' '}·{' '}
                                                {sessionNameFromQuery || assessmentData?.classroom_name || assessmentData?.session_name}
                                            </>
                                        ) : null}
                                    </p>
                                </div>

                                <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-semibold border ${
                                    submissionResult.percentage >= submissionResult.pass_percentage
                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                        : 'bg-red-50 text-red-700 border-red-100'
                                }`}>
                                    <Icon icon={submissionResult.percentage >= submissionResult.pass_percentage ? 'mdi:check-circle' : 'mdi:close-circle'} width="18" height="18" />
                                    {submissionResult.percentage >= submissionResult.pass_percentage ? 'Passed' : 'Failed'}
                                </span>
                            </div>
                        </div>

                        <div className="p-6 sm:p-8">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="rounded-2xl border border-gray-100 bg-white p-4">
                                    <p className="text-xs font-medium text-gray-500">Score</p>
                                    <p className="text-2xl font-bold text-[#7083FF] mt-1">{submissionResult.percentage}%</p>
                                </div>
                                <div className="rounded-2xl border border-gray-100 bg-white p-4">
                                    <p className="text-xs font-medium text-gray-500">Pass percentage</p>
                                    <p className="text-2xl font-bold text-gray-900 mt-1">{submissionResult.pass_percentage}%</p>
                                </div>
                                <div className="rounded-2xl border border-gray-100 bg-white p-4">
                                    <p className="text-xs font-medium text-gray-500">Attempted</p>
                                    <p className="text-2xl font-bold text-gray-900 mt-1">{submissionResult.total_attempted}/{submissionResult.total_questions}</p>
                                </div>
                                <div className="rounded-2xl border border-gray-100 bg-white p-4">
                                    <p className="text-xs font-medium text-gray-500">Correct</p>
                                    <p className="text-2xl font-bold text-gray-900 mt-1">{submissionResult.total_correct}</p>
                                </div>
                            </div>

                            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
                                <button
                                    type="button"
                                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition"
                                    onClick={() => navigate(-1)}
                                >
                                    <Icon icon="mdi:arrow-left" width="20" height="20" />
                                    Back
                                </button>
                                <button
                                    type="button"
                                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-white bg-[#7083FF] hover:bg-[#5f73e6] transition"
                                    onClick={() => navigate(`/user/AllClassroom/classroomDetails/${encodeURIComponent(encodeData(classroom_id))}`)}
                                >
                                    <Icon icon="mdi:school" width="20" height="20" />
                                    Back to Classroom
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Loading
    if (!assessmentData) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-4xl mx-auto py-12 px-4">
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
                        <div className="flex flex-col items-center justify-center">
                            <div className="h-10 w-10 rounded-full border-2 border-gray-200 border-t-[#7083FF] animate-spin" />
                            <p className="mt-4 text-sm text-gray-500">Loading assessment...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // No Questions Added Yet
    if (!assessmentData?.questions?.length) {
        return (
            <div className="max-w-7xl mx-auto py-5 px-4">
                <div className="flex justify-center">
                    <div className="w-full md:w-1/2">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                            <div className="text-center p-5">
                                <div className="mb-4">
                                    <Icon
                                        icon="mdi:file-document-outline"
                                        className="text-gray-500"
                                        width="80"
                                        height="80"
                                    />
                                </div>
                                <h4 className="text-gray-500 mb-3">No Assessment Questions Available</h4>
                                <p className="text-gray-500 mb-4">The instructor hasn't added any questions to this assessment yet.</p>
                                <button
                                    className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg flex items-center gap-2 mx-auto"
                                    onClick={() => navigate(`/user/AllClassroom/classroomDetails/${encodeURIComponent(encodeData(classroom_id))}`)}
                                >
                                    <Icon icon="mdi:arrow-left" width="20" height="20" />
                                    Back to Classroom
                                </button>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border-0 bg-yellow-50 mt-4">
                            <div className="p-4">
                                <div className="flex items-center gap-3">
                                    <Icon
                                        icon="mdi:information"
                                        className="text-yellow-600"
                                        width="24"
                                        height="24"
                                    />
                                    <div>
                                        <h6 className="mb-1">Assessment Due Date</h6>
                                        <p className="mb-0 text-sm">
                                            Due by: {new Date(assessmentData.due_date).toLocaleString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true
                                            })}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const currentQuestion = assessmentData.questions[currentQuestionIndex];
    const isLastQuestion = currentQuestionIndex === assessmentData.questions.length - 1;

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
                {/* Back Button */}
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#7083FF] transition-colors py-2 px-2 rounded-lg hover:bg-white mb-4"
                >
                    <Icon icon="mdi:arrow-left" width="20" height="20" />
                    Back
                </button>

                <div className="mb-5">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                        <div>
                            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">{assessmentData.assessment_name}</h1>
                            {(sessionNameFromQuery || assessmentData?.classroom_name || assessmentData?.session_name) && (
                                <p className="text-sm text-gray-500 mt-1">
                                    Session: {sessionNameFromQuery || assessmentData?.classroom_name || assessmentData?.session_name}
                                </p>
                            )}
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border border-gray-200 text-gray-700 bg-white">
                                <Icon icon="mdi:help-circle" width="16" height="16" className="text-[#7083FF]" />
                                {currentQuestionIndex + 1} / {assessmentData.total_questions}
                            </span>

                            {timeLeft !== null ? (
                                <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border ${
                                    timeLeft <= 60 ? 'border-red-200 bg-red-50 text-red-700' : 'border-blue-200 bg-blue-50 text-blue-700'
                                }`}>
                                    <Icon icon="mdi:clock-outline" width="16" height="16" />
                                    {formatTime(timeLeft)}
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border border-emerald-200 bg-emerald-50 text-emerald-700">
                                    <Icon icon="mdi:clock-outline" width="16" height="16" />
                                    Untimed
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="mt-4">
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-[#7083FF] rounded-full transition-all"
                                style={{ width: `${progress}%` }}
                                role="progressbar"
                                aria-valuenow={progress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="p-5 sm:p-6">
                        <div className="flex items-start justify-between gap-3">
                            <h2 className="text-base sm:text-lg font-semibold text-gray-900 leading-snug">
                                {currentQuestion?.question_name}
                            </h2>
                            <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#7083FF]/10 text-[#7083FF] font-bold">
                                {currentQuestionIndex + 1}
                            </span>
                        </div>

                        <div className="mt-5 space-y-3">
                            {currentQuestion?.options?.map((option) => {
                                const isSelected = selectedAnswer === option.id;
                                return (
                                    <button
                                        key={option.id}
                                        type="button"
                                        onClick={() => handleOptionSelect(option.id)}
                                        className={`w-full text-left rounded-2xl border p-4 transition ${
                                            isSelected
                                                ? 'border-[#7083FF] bg-[#7083FF]/5 shadow-sm'
                                                : 'border-gray-200 bg-white hover:bg-gray-50'
                                        }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <span className={`mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full border ${
                                                isSelected ? 'border-[#7083FF] bg-[#7083FF] text-white' : 'border-gray-300 bg-white'
                                            }`}>
                                                {isSelected ? <Icon icon="mdi:check" width="14" height="14" /> : null}
                                            </span>
                                            <span className="text-sm sm:text-base text-gray-800">{option.option_value}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="p-5 sm:p-6 bg-gray-50/40 border-t border-gray-100">
                        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                            <button
                                type="button"
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                onClick={handlePrevious}
                                disabled={currentQuestionIndex === 0}
                            >
                                <Icon icon="mdi:arrow-left" width="20" height="20" />
                                Previous
                            </button>

                            {isLastQuestion ? (
                                <button
                                    type="button"
                                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-white bg-[#7083FF] hover:bg-[#5f73e6] disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                                    onClick={() => handleSubmit(false)}
                                    disabled={!selectedAnswer || isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-white/40 border-t-white" role="status" aria-hidden="true" />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <Icon icon="mdi:check-circle" width="20" height="20" />
                                            Submit
                                        </>
                                    )}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-white bg-[#7083FF] hover:bg-[#5f73e6] disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                                    onClick={handleNext}
                                    disabled={!selectedAnswer}
                                >
                                    Next
                                    <Icon icon="mdi:arrow-right" width="20" height="20" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AssessmentQuiz;